import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DateTimeRangePickerProps {
  startDate: Date;
  endDate: Date;
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  startDate,
  endDate,
  onDateRangeChange
}) => {
  // Auto-correct endDate to current time if it's set to end of day
  React.useEffect(() => {
    const now = new Date();
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    
    if (Math.abs(endDate.getTime() - endOfDay.getTime()) < 1000) {
      onDateRangeChange(startDate, now);
    }
  }, []);
  const [selectedTimeRange, setSelectedTimeRange] = useState('last_7_days');

  const timeRangeOptions = [
    { value: 'last_5_minutes', label: 'Last 5 minutes' },
    { value: 'last_30_minutes', label: 'Last 30 minutes' },
    { value: 'last_1_hour', label: 'Last 1 hour' },
    { value: 'last_3_hours', label: 'Last 3 hours' },
    { value: 'last_6_hours', label: 'Last 6 hours' },
    { value: 'last_12_hours', label: 'Last 12 hours' },
    { value: 'last_1_day', label: 'Last 1 day' },
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'last_2_weeks', label: 'Last 2 weeks' },
    { value: 'last_1_month', label: 'Last 1 month' },
    { value: 'last_3_months', label: 'Last 3 months' },
    { value: 'last_6_months', label: 'Last 6 months' },
    { value: 'last_12_months', label: 'Last 12 months' },
    { value: 'custom', label: 'Custom' }
  ];

  const handleTimeRangeChange = (value: string) => {
    setSelectedTimeRange(value);
    if (value === 'custom') return;
    
    const now = new Date();
    const end = new Date(now);
    const start = new Date(now);
    
    switch (value) {
      case 'last_5_minutes':
        start.setMinutes(now.getMinutes() - 5);
        break;
      case 'last_30_minutes':
        start.setMinutes(now.getMinutes() - 30);
        break;
      case 'last_1_hour':
        start.setHours(now.getHours() - 1);
        break;
      case 'last_3_hours':
        start.setHours(now.getHours() - 3);
        break;
      case 'last_6_hours':
        start.setHours(now.getHours() - 6);
        break;
      case 'last_12_hours':
        start.setHours(now.getHours() - 12);
        break;
      case 'last_1_day':
        start.setDate(now.getDate() - 1);
        break;
      case 'last_7_days':
        start.setDate(now.getDate() - 7);
        break;
      case 'last_2_weeks':
        start.setDate(now.getDate() - 14);
        break;
      case 'last_1_month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'last_3_months':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'last_6_months':
        start.setMonth(now.getMonth() - 6);
        break;
      case 'last_12_months':
        start.setFullYear(now.getFullYear() - 1);
        break;
    }
    
    onDateRangeChange(start, end);
  };

  const handleDateChange = (date: Date | null, isStart: boolean) => {
    if (isStart) {
      onDateRangeChange(date || new Date(), endDate);
    } else {
      onDateRangeChange(startDate, date || new Date());
    }
    setSelectedTimeRange('custom');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', maxWidth: '600px' }}>
        <select
          value={selectedTimeRange}
          onChange={(e) => handleTimeRangeChange(e.target.value)}
          className="time-range-select"
        >
          {timeRangeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <DatePicker
          selected={startDate}
          onChange={(date) => handleDateChange(date, true)}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat="dd/MM/yyyy HH:mm"
          className="date-input"
        />
        <span style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>to</span>
        <DatePicker
          selected={endDate}
          onChange={(date) => handleDateChange(date, false)}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat="dd/MM/yyyy HH:mm"
          className="date-input"
        />
      </div>
      
      <style>{`
        .time-range-select {
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }
        
        .date-input {
          padding: 0.25rem 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 150px;
          font-size: 14px;
        }
        
        .react-datepicker-wrapper {
          display: inline-block;
        }
        
        .react-datepicker__input-container input {
          padding: 0.25rem 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 150px;
          font-size: 14px;
          background: white;
        }
        
        .react-datepicker__input-container input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
        
        .react-datepicker {
          font-family: inherit;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          background: white;
          z-index: 9999 !important;
        }
        
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
        
        .react-datepicker__tab-loop {
          z-index: 9999 !important;
        }
        
        .react-datepicker__header {
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          border-radius: 8px 8px 0 0;
          padding: 8px 0;
        }
        
        .react-datepicker__current-month {
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }
        
        .react-datepicker__day-name {
          color: #6c757d;
          font-weight: 500;
        }
        
        .react-datepicker__day {
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        
        .react-datepicker__day:hover {
          background-color: #e9ecef;
        }
        
        .react-datepicker__day--selected {
          background-color: #007bff;
          color: white;
        }
        
        .react-datepicker__day--selected:hover {
          background-color: #0056b3;
        }
        
        .react-datepicker__day--today {
          background-color: #fff3cd;
          color: #856404;
        }
        
        .react-datepicker__time-container {
          border-left: 1px solid #e9ecef;
        }
        
        .react-datepicker__time-list-item {
          padding: 8px 12px;
          transition: background-color 0.2s ease;
        }
        
        .react-datepicker__time-list-item:hover {
          background-color: #f8f9fa;
        }
        
        .react-datepicker__time-list-item--selected {
          background-color: #007bff;
          color: white;
        }
        
        .react-datepicker__navigation {
          border: none;
          background: none;
        }
        
        .react-datepicker__navigation:hover {
          background-color: #e9ecef;
          border-radius: 4px;
        }
      `}</style>
    </>
  );
};

export default DateTimeRangePicker;